import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';
import { FaEdit, FaTrash, FaKey } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.milstudio.in';

console.log('API_URL resolved to:', API_URL);

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showTablePopup, setShowTablePopup] = useState(false);
  const [showSafeTablePopup, setShowSafeTablePopup] = useState(false);
  const [availableTables, setAvailableTables] = useState([]);
  const [safeTables, setSafeTables] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [selectedSafeUser, setSelectedSafeUser] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/list-stafflogin.php`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          const mappedUsers = data.data.map((user) => ({
            id: user.id,
            name: user.name,
            type: user.role_name || '',
            phone: user.phone,
            email: user.email,
            createdAt: user.create_at,
          }));
          setUsers(mappedUsers);
        } else {
          showToast('Failed to fetch users: Invalid data format', 'error');
        }
      } catch (error) {
        showToast('Failed to fetch users: ' + error.message, 'error');
      }
    };
    fetchUsers();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.includes(searchTerm);
    return matchesSearch;
  });

  const getTypeBadgeClass = (type) => {
    switch (type.toLowerCase()) {
      case 'waiter':
        return 'badge-waiter';
      case 'cashier':
        return 'badge-cashier';
      case 'safe':
        return 'badge-safe';
      default:
        return 'badge-default';
    }
  };

  const handleEdit = (user) => {
    navigate(`/edit-user/${user.id}`, { state: { user } });
    showToast(`Editing user: ${user.name}`, 'info');
  };

  const handleDelete = (userId) => {
    const user = users.find(u => u.id === userId);
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const response = await fetch(`${API_URL}delete-stafflogin.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(userToDelete.id) }),
      });
      const data = await response.json();
      if (data.status) {
        setUsers(users.filter(user => user.id !== userToDelete.id));
        showToast(`User "${userToDelete.name}" deleted successfully`, 'success');
      } else {
        showToast('Failed to delete user: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Failed to delete user: ' + error.message, 'error');
    }
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleChangePassword = (user) => {
    setSelectedUserForPassword(user);
    setShowChangePasswordModal(true);
  };

  const confirmChangePassword = async (password) => {
    if (!selectedUserForPassword || !password.trim()) {
      showToast('Please enter a new password', 'error');
      return;
    }
    if (password.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/change-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staff_id: Number(selectedUserForPassword.id),
          new_password: password
        }),
      });
      const data = await response.json();
      if (data.status) {
        showToast(`Password changed successfully for ${selectedUserForPassword.name}`, 'success');
        setShowChangePasswordModal(false);
        setSelectedUserForPassword(null);
      } else {
        showToast('Failed to change password: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Failed to change password: ' + error.message, 'error');
    }
  };

  const handleAddUser = () => {
    navigate('/add-user');
    showToast('Navigating to add new user', 'info');
  };

  const fetchAvailableTables = async () => {
    try {
      const response = await fetch(`${API_URL}get-available-tables.php`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      if (data.status && Array.isArray(data.available_tables)) {
        setAvailableTables(data.available_tables);
      } else {
        setAvailableTables([]);
        showToast('Failed to fetch tables: Invalid data format', 'error');
      }
    } catch (error) {
      setAvailableTables([]);
      showToast('Failed to fetch tables: ' + error.message, 'error');
    }
  };

  const fetchSafeTables = async () => {
    try {
      const response = await fetch(`${API_URL}/list-table.php`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        setSafeTables(data.data);
      } else {
        setSafeTables([]);
        showToast('Failed to fetch tables: Invalid data format', 'error');
      }
    } catch (error) {
      setSafeTables([]);
      showToast('Failed to fetch tables: ' + error.message, 'error');
    }
  };

  // Open popup for waiter
  const handleShowTablePopup = (user) => {
    console.log('Assign Table clicked for:', user);
    setSelectedWaiter(user);
    setShowTablePopup(true);
    fetchAvailableTables();
  };

  // Open popup for Safe user
  const handleShowSafeTablePopup = (user) => {
    console.log('Assign Table clicked for Safe user:', user);
    setSelectedSafeUser(user);
    setShowSafeTablePopup(true);
    fetchSafeTables();
  };

  // Close popup
  const handleCloseTablePopup = () => {
    setShowTablePopup(false);
    setSelectedWaiter(null);
    setAvailableTables([]);
  };

  // Close Safe popup
  const handleCloseSafeTablePopup = () => {
    setShowSafeTablePopup(false);
    setSelectedSafeUser(null);
    setSafeTables([]);
  };

  const ChairSelectionModal = ({ open, onClose, onSave, tableName, chairs }) => {
    const [selected, setSelected] = React.useState([]);

    React.useEffect(() => {
      setSelected([]); 
    }, [open]);

    const handleToggle = (label) => {
      setSelected((prev) =>
        prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
      );
    };

    if (!open) return null;
    const displayChairs = (chairs && chairs.length > 0) ? chairs : [
      { label: 'Table 1' },
      { label: 'Table 2' },
      { label: 'Table 3' },
      { label: 'Table 4' },
    ];
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 className="modal-title">Select Tables for User: ({tableName})</h2>
          <div className="chair-card-list grid-3">
            {displayChairs.map((chair, idx) => {
              const isChecked = selected.includes(chair.label);
              return (
                <div
                  className={`chair-card table-card-selectable${isChecked ? ' selected' : ''}`}
                  key={idx}
                  onClick={() => handleToggle(chair.label)}
                  style={{ position: 'relative', cursor: 'pointer' }}
                >
                  <span className="table-icon" role="img" aria-label="table">🛋️</span>
                  <span className="chair-label">{chair.label}</span>
                  {isChecked && (
                    <span className="checkmark-circle">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" fill="#2563eb" stroke="#fff" strokeWidth="2"/>
                        <path d="M8 12.5l2.5 2.5 5-5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-primary" onClick={() => onSave(selected)}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  const SafeTableSelectionModal = ({ open, onClose, onSave, tableName, tables }) => {
    const [selected, setSelected] = React.useState([]);

    React.useEffect(() => {
      setSelected([]); 
    }, [open]);

    const handleToggle = (tableId) => {
      setSelected((prev) =>
        prev.includes(tableId) ? prev.filter(id => id !== tableId) : [...prev, tableId]
      );
    };

    if (!open) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 className="modal-title">Select Tables for Safe User: ({tableName})</h2>
          <div className="chair-card-list grid-3">
            {tables.map((table, idx) => {
              const isChecked = selected.includes(table.id);
              return (
                <div
                  className={`chair-card table-card-selectable${isChecked ? ' selected' : ''}`}
                  key={idx}
                  onClick={() => handleToggle(table.id)}
                  style={{ position: 'relative', cursor: 'pointer' }}
                >
                  <span className="table-icon" role="img" aria-label="table">🛋️</span>
                  <span className="chair-label">{table.tablename} ({table.room_type_name})</span>
                  {table.assigned_chairs && table.assigned_chairs.length > 0 && (
                    <div className="chair-details">
                      <small>Chairs: {table.assigned_chairs.map(c => c.chairname).join(', ')}</small>
                    </div>
                  )}
                  {isChecked && (
                    <span className="checkmark-circle">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" fill="#2563eb" stroke="#fff" strokeWidth="2"/>
                        <path d="M8 12.5l2.5 2.5 5-5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-primary" onClick={() => onSave(selected)}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  const DeleteConfirmationModal = ({ open, user, onConfirm, onCancel }) => {
    if (!open || !user) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Confirm Delete</h2>
          <p>Are you sure you want to delete <b>{user.name}</b>?</p>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onCancel}>No</button>
            <button className="btn btn-danger" onClick={onConfirm}>Yes</button>
          </div>
        </div>
      </div>
    );
  };

  const ChangePasswordModal = memo(({ open, user, onConfirm, onCancel }) => {
    const [password, setPassword] = useState('');
    useEffect(() => { setPassword(''); }, [open, user]);
    if (!open || !user) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Change Password</h2>
          <p>Enter new password for <b>{user.name}</b></p>
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              minLength="6"
              autoComplete="new-password"
            />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={() => onConfirm(password)}>Change Password</button>
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="user-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>User Management</h1>
          <p>Manage restaurant staff and users</p>
        </div>
        <button className="add-unit-button" onClick={handleAddUser}>
          + Add New User
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users by name, type, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="unit-info">
                    <div className="unit-icon">
                      👤
                    </div>
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>
                  {['cashier', 'waiter', 'safe'].includes(user.type.toLowerCase()) ? (
                    <span className={`type-badge ${getTypeBadgeClass(user.type)}`}>
                      {user.type}
                    </span>
                  ) : (
                    <span>{user.type}</span>
                  )}
                </td>
                <td>{user.phone}</td>
                <td>{user.email}</td>
                <td>
                  <div className="action-buttons">
                    {user.type.toLowerCase() === 'waiter' && (
                      <button 
                        className="action-btn plus assign-table-btn"
                        title="Assign Table"
                        onClick={() => handleShowTablePopup(user)}
                      >
                        + Assign Table
                      </button>
                    )}
                    {user.type === 'Safe' && (
                      <button 
                        className="action-btn plus assign-table-btn"
                        title="Assign Table"
                        onClick={() => handleShowSafeTablePopup(user)}
                      >
                        + Assign Table
                      </button>
                    )}
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(user)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="action-btn change-password"
                      onClick={() => handleChangePassword(user)}
                      title="Change Password"
                    >
                      <FaKey />
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(user.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}


      <ChairSelectionModal
        open={showTablePopup}
        onClose={handleCloseTablePopup}
        onSave={async (selectedLabels) => {
          const selectedTables = availableTables.filter(t => selectedLabels.includes(t.tablename));
          const table_ids = selectedTables.map(t => Number(t.id));
          const staff_id = selectedWaiter?.id;
          const assign_date = new Date().toISOString().slice(0, 10); 
          try {
            const response = await fetch(`${API_URL}assign-tables.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ staff_id, table_ids, assign_date })
            });
            const data = await response.json();
            if (data.status) {
              showToast('Tables assigned successfully!', 'success');
              setShowTablePopup(false);
            } else {
              showToast('Failed to assign tables: ' + (data.message || 'Unknown error'), 'error');
            }
          } catch (error) {
            showToast('Failed to assign tables: ' + error.message, 'error');
          }
        }}
        tableName={selectedWaiter?.name || ''}
        chairs={availableTables.length > 0 ? availableTables.map(t => ({ label: t.tablename })) : [{ label: 'No Tables found' }]}
      />

      <SafeTableSelectionModal
        open={showSafeTablePopup}
        onClose={handleCloseSafeTablePopup}
        onSave={async (selectedTableIds) => {
          if (!selectedSafeUser?.id) {
            showToast('No Safe user selected', 'error');
            return;
          }
          if (!selectedTableIds || selectedTableIds.length === 0) {
            showToast('No tables selected', 'error');
            return;
          }
   
          const validTableIds = safeTables.map(table => table.id);
          const invalidTableIds = selectedTableIds.filter(id => !validTableIds.includes(String(id)));
          if (invalidTableIds.length > 0) {
            showToast(`Invalid table IDs: ${invalidTableIds.join(', ')}`, 'error');
            return;
          }
          const staff_id = Number(selectedSafeUser.id); 
          const table_ids = selectedTableIds.map(id => Number(id)); 
          const assign_date = new Date().toISOString().slice(0, 10);
          const requestBody = { staff_id, table_ids, assign_date };
          try {
            const response = await fetch(`${API_URL}/assign-table-to-safe.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody)
            });
            const responseText = await response.text();
            let data;
            try {
              data = JSON.parse(responseText);
            } catch (parseError) {
              showToast('Invalid response format from server', 'error');
              return;
            }
            if (data.status) {
              showToast('Tables assigned successfully!', 'success');
              setShowSafeTablePopup(false);
              fetchSafeTables();
            } else {
              showToast('Failed to assign tables: ' + (data.message || 'Unknown error'), 'error');
            }
          } catch (error) {
            showToast('Failed to assign tables: ' + error.message, 'error');
          }
        }}
        tableName={selectedSafeUser?.name || ''}
        tables={safeTables.length > 0 ? safeTables : []}
      />
      <DeleteConfirmationModal
        open={showDeleteModal}
        user={userToDelete}
        onConfirm={confirmDeleteUser}
        onCancel={() => { setShowDeleteModal(false); setUserToDelete(null); }}
      />
      <ChangePasswordModal
        open={showChangePasswordModal}
        user={selectedUserForPassword}
        onConfirm={confirmChangePassword}
        onCancel={() => { 
          setShowChangePasswordModal(false); 
          setSelectedUserForPassword(null); 
        }}
      />
    </div>
  );
};

export default UserList; 